/**
 * Cart context for the public storefront.
 *
 * Persists the cart in localStorage so it survives reloads. Each item stores
 * the minimal data needed to render the cart drawer without a server round
 * trip; the backend always re-validates prices and stock when creating the
 * Mercado Pago preference.
 *
 * Items are stored in their *original* currency (matching `sale_currency`
 * on the product). The drawer/checkout convert to MXN using the live
 * exchange rate from /api/public/checkout/config, since Mercado Pago México
 * only accepts MXN.
 */
import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";

const STORAGE_KEY = "alendra_cart_v1";

const CartContext = createContext(null);

function loadFromStorage() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((i) => i && i.product_id && i.qty > 0);
  } catch {
    return [];
  }
}

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => loadFromStorage());
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Persist on every change
  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(items)); } catch { /* ignore */ }
  }, [items]);

  const addItem = useCallback((product, qty = 1) => {
    if (!product?.id) return;
    setItems((prev) => {
      const idx = prev.findIndex((i) => i.product_id === product.id);
      const maxQty = Math.max(1, Number(product.max_qty || 1));
      if (idx >= 0) {
        const next = [...prev];
        const newQty = Math.min(maxQty, next[idx].qty + qty);
        next[idx] = { ...next[idx], qty: newQty, max_qty: maxQty };
        return next;
      }
      return [
        ...prev,
        {
          product_id: product.id,
          name: product.name,
          brand: product.brand || "",
          photo: (product.photos || [])[0] || null,
          unit_price: Number(product.store_price || 0),
          currency: product.sale_currency || "MXN",
          qty: Math.min(maxQty, Math.max(1, qty)),
          max_qty: maxQty,
          kind: product.kind === "combo" ? "combo" : "product",
        },
      ];
    });
    setDrawerOpen(true);
  }, []);

  const updateQty = useCallback((product_id, qty) => {
    setItems((prev) =>
      prev
        .map((i) => {
          if (i.product_id !== product_id) return i;
          const max = Math.max(1, Number(i.max_qty || 1));
          const next = Math.min(max, Math.max(0, qty));
          return { ...i, qty: next };
        })
        .filter((i) => i.qty > 0),
    );
  }, []);

  const removeItem = useCallback((product_id) => {
    setItems((prev) => prev.filter((i) => i.product_id !== product_id));
  }, []);

  const clear = useCallback(() => setItems([]), []);

  const count = useMemo(() => items.reduce((s, i) => s + i.qty, 0), [items]);

  const value = useMemo(
    () => ({ items, count, drawerOpen, setDrawerOpen, addItem, updateQty, removeItem, clear }),
    [items, count, drawerOpen, addItem, updateQty, removeItem, clear],
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used inside CartProvider");
  return ctx;
}
